<template>
    <div class="flex flex-col h-full">
        <page-header :buttons="pageActions.buttons" :additional="pageActions.additional" @method="methodHandler">
            <template slot="title">{{ item.name }}<small class="text-xs ml-2 hidden xl:inline-block">Suppliers</small></template>
        </page-header>

        <tabs :tabs="tabs" :active="startTab" @setTab="setActiveTab" ref="tabs"></tabs>

        <div class="flex xl:flex-row lg:flex-col md:flex-col sm:flex-col flex-grow">
            <div class="absolute top-0 left-0 right-0 bottom-0 w-full overflow-hidden flex items-center text-center justify-center z-50" v-show="loading"><spinner line-fg-color="#922C49" size="55" :line-size="5"></spinner></div>
            <div class="lg:w-full md:w-full sm:w-full px-4 sm:px-6 lg:px-6 bg-white border-r border-gray-400 bg-gray-200" :class="[ activeTab === 'communications' ? 'xl:w-full' : 'xl:w-3/4' ]" v-if="!loading">
                <div class="mt-4 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12" v-if="!loading" v-show="activeTab === 'details'">
                    <div class="sm:col-span-6">
                        <table class="w-full mb-4 internal-table">
                            <thead>
                            <tr>
                                <th colspan="2">Supplier Details</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr>
                                <td class="w-32 font-black">Name</td>
                                <td>{{ item.name }}</td>
                            </tr>
                            <tr>
                                <td>Street Address</td>
                                <td>{{ item.street_address_1 }}<span v-if="item.street_address_2">, {{item.street_address_2}}</span> {{ item.street_suburb }} {{ item.street_state }}  {{ item.street_postcode }}</td>
                            </tr>
                            <tr>
                                <td>Postal Address</td>
                                <td>{{ item.postal_address_1 }}<span v-if="item.postal_address_2">, {{item.postal_address_2}}</span> {{ item.postal_suburb }} {{ item.postal_state }}  {{ item.postal_postcode }}</td>
                            </tr>
                            <tr>
                                <td>ABN</td>
                                <td>{{ item.abn }}</td>
                            </tr>
                            <tr>
                                <td>ACN</td>
                                <td>{{ item.acn }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="sm:col-span-6">
                        <table class="w-full mb-4 internal-table">
                            <thead>
                                <tr>
                                    <th colspan="2">Contact Information</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Contact</td>
                                    <td>{{ item.first }} {{ item.last }}</td>
                                </tr>
                                <tr>
                                    <td>Mobile</td>
                                    <td>{{ item.mobile }}</td>
                                </tr>
                                <tr>
                                    <td>Phone</td>
                                    <td>{{ item.phone }}</td>
                                </tr>
                                <tr>
                                    <td>Website</td>
                                    <td><a :href="item.website" target="_blank">{{ item.website }}</a></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <notes v-if="!loading"
                    v-show="activeTab === 'notes'"
                    :id="item.id"
                    :notes="item.notes"
                    model="Supplier" />
            </div>
            <div class="xl:w-1/4 lg:w-full md:w-full sm:w-full px-4 sm:px-6 lg:px-6" v-if="activeTab !== 'communications'">
                <div class="mt-4 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12" v-if="!loading">
                    <div class="sm:col-span-12">
                        <table class="w-full internal-table">
                            <thead>
                                <tr>
                                    <th colspan="2">Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="w-full xl:w-24">Exp. Account</td>
                                    <td>{{ item.expense_account }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="sm:col-span-12">
                        <table class="w-full mb-6 internal-table">
                            <thead>
                            <tr>
                                <th colspan="2">Meta</th>
                            </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="w-full xl:w-24">Last Updated</td>
                                    <td>{{ new Date(item.meta.updated_at) | moment("DD/MM/YYYY h:mm a") }}</td>
                                </tr>
                                <tr>
                                    <td>Created At</td>
                                    <td>{{ new Date(item.meta.created_at) | moment("DD/MM/YYYY h:mm a") }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
        <delete-confirmation-modal v-if="showDeleteModal" @close="closeDeleteModal" @delete="deleteItem" ref="itemDeleteModal">
            <template slot="content" v-if="item.friendly_id">Are you sure you want to delete <strong>#{{ item.name }}</strong>?</template>
        </delete-confirmation-modal>

        <email-modal v-if="showEmailModal"
            :to="{ email: item.email, name: item.first+' '+item.last, locked: true }"
            @close="showEmailModal = false"
            @sent="emailSent"
            model="Supplier"
            :id="item.id"
        />

    </div>
</template>

<script>
    import * as router from "vue-router";
    import axios from "axios";
    import Tabs from "@/mixins/Tabs";
    import Notes from "@/components/Notes";
    import DeleteModal from "@/mixins/DeleteModal";
    import EmailModal from "@/components/modals/EmailModal";

    export default {
        name: "SupplierView",
        components: {
            Notes,
            EmailModal
        },
        props: {
            id: { required: true },
            return: { required: false, default: '/inventory/suppliers' },
            page: { required: false, default: 'suppliers' }
        },
        mixins: [
            Tabs,
            DeleteModal
        ],
        data: function () {
            return {
                resource: 'suppliers',
                member: {},
                startTab: null,
                loading: true,
                item: {},
                pageActions: {},
                showEmailModal: false,
            }
        },
        methods: {
            emailSent: function(){
                this.showEmailModal = false;
                this.getData();
            },
            smsSent: function(){
                this.showSmsModal = false;
                this.getData();
            },
            getData: function(){
                this.$Progress.start();

                axios.get(process.env.VUE_APP_API_URL + '/suppliers/'+this.id)
                .then(response => {
                    this.item = response.data;

                    this.pageActions = {
                        buttons: [
                            {
                                label: 'Send Email',
                                icon: 'paper-plane',
                                type: 'method',
                                iconClass: 'text-green-700',
                                method: 'openEmailEditor',
                                display: this.item.email && !this.item.date_closed && !this.item.date_converted
                            },
                            {
                                label: 'Edit',
                                icon: 'edit',
                                type: 'router-link',
                                link: '/inventory/suppliers/'+ this.id + '/edit',
                                iconClass: 'text-gray-700',
                                display: !!(this.$store.getters.permissions.indexOf('suppliers.edit') !== -1 || this.permissions.indexOf('suppliers.edit') !== -1 || this.$store.getters.nimdarepus)
                            },
                            {
                                label: 'Close',
                                icon: 'times',
                                type: 'router-link',
                                iconClass: 'text-red-500',
                                link: this.return ? this.return : '/inventory/suppliers',
                            },
                        ],
                        additional: [
                            {
                                label: 'Delete',
                                icon: 'trash-alt',
                                type: 'method',
                                method: 'deleteModal',
                                iconClass: 'text-red-500',
                                display: !!(this.$store.getters.permissions.indexOf('suppliers.destroy') !== -1 || this.$store.getters.nimdarepus)
                            }
                        ]
                    };

                    this.$Progress.finish();
                    this.loading = false;
                })
                .catch(() => {
                    this.$Progress.finish();
                    this.loading = false;
                });
            },
            methodHandler: function (method) {
                this[method]();
            },
            actionHandler: function (data) {
                this[data.method](data);
            },
            openEmailEditor: function(data) {
                this.showEmailModal = true;
            }
        },
        beforeMount() {

        },
        mounted() {
            this.getData();
        },
        computed: {
            tabs() {
                return [
                    {
                        name: 'Details',
                        slug: 'details'
                    },
                    {
                        name: 'Notes',
                        slug: 'notes',
                    },
                    {
                        name: 'Tasks',
                        slug: 'tasks'
                    },
                    {
                        name: 'Communications',
                        slug: 'communications',
                    },
                ];
            }
        }
    }
</script>

<style scoped>

</style>
